import { Injectable, inject } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, distinctUntilChanged, shareReplay } from 'rxjs/operators';

import { TaskModel } from '../models/task.model';
import * as fromRoot from '../redux';

@Injectable({
  providedIn: 'root',
})
export class StoreTaskService {
  private store = inject<Store<fromRoot.State>>(Store);

  getOpenTask() {
    return this._prepareOne(this.store.pipe(select(fromRoot.getTaskOpen))).pipe(
      // Добавляем shareReplay для повторного использования последнего значения
      // и предотвращения повторных вычислений при множественных подписках
      shareReplay(1)
    );
  }

  getTaskListByStatusId(statusId: string) {
    return this._prepareAll(this.store.pipe(select(fromRoot.getTaskEntitiesByStatus(statusId))));
  }

  getTaskList() {
    return this._prepareAll(this.store.pipe(select(fromRoot.getTaskEntities)));
  }

  private _prepareAll(taskList$: Observable<TaskModel[]>) {
    const inviteList$ = this.store.pipe(select(fromRoot.getInviteEntities));
    const statusList$ = this.store.pipe(select(fromRoot.getStatusEntities));
    const tagList$ = this.store.pipe(select(fromRoot.getTagEntities));

    return combineLatest([taskList$, inviteList$, statusList$, tagList$]).pipe(
      filter(([taskList, inviteList, statusList, tagList]) => inviteList.length > 0 && statusList.length > 0),
      map(([taskList, inviteList, statusList, tagList]) =>
        taskList.map((task: TaskModel) => {
          const updatedTask = { ...task };

          if (task.responsibleId) {
            updatedTask.responsible = inviteList.find(item => item.userId === task.responsibleId);
          }

          if (task.statusId) {
            updatedTask.status = statusList.find(item => item.id === task.statusId);
          }

          updatedTask.tagList = task.tags
            .map(tagId => tagList.find(tag => tag.id === tagId))
            .filter(tag => tag !== undefined);

          return updatedTask as TaskModel;
        })
      )
    );
  }

  private _prepareOne(task$: Observable<TaskModel>) {
    const inviteList$ = this.store.pipe(select(fromRoot.getInviteEntities));
    const statusList$ = this.store.pipe(select(fromRoot.getStatusEntities));
    const tagList$ = this.store.pipe(select(fromRoot.getTagEntities));

    return combineLatest([task$, inviteList$, statusList$, tagList$]).pipe(
      filter(([task, inviteList, statusList]) => task && inviteList.length > 0 && statusList.length > 0),
      map(([task, inviteList, statusList, tagList]) => {
        // Создаем глубокую копию задачи
        const preparedTask = Object.assign(new TaskModel(), task);

        // Заполняем связанные данные через иммутабельные операции
        if (preparedTask.responsibleId) {
          preparedTask.responsible = inviteList.find(item => item.userId === preparedTask.responsibleId);
        }

        if (preparedTask.userIdArchive) {
          preparedTask.userArchive = inviteList.find(item => item.userId === preparedTask.userIdArchive);
        }

        if (preparedTask.userIdComplete) {
          preparedTask.userComplete = inviteList.find(item => item.userId === preparedTask.userIdComplete);
        }

        preparedTask.status = statusList.find(item => item.id === preparedTask.statusId);

        // Иммутабельное обновление массивов
        preparedTask.followersUsers = preparedTask.followers
          .map(followerId => inviteList.find(item => item.userId === followerId))
          .filter(Boolean)
          .map(invite => invite.user);

        preparedTask.tagList = preparedTask.tags.map(tagId => tagList.find(tag => tag.id === tagId)).filter(Boolean);

        return preparedTask;
      }),
      // Добавляем distinctUntilChanged чтобы избежать повторной эмиссии идентичных задач
      distinctUntilChanged((prev, current) => {
        // Сравниваем только ключевые поля задачи для определения, действительно ли она изменилась
        if (!prev || !current) {
          return false;
        }
        if (prev.id !== current.id) {
          return false;
        }
        if (prev.updatedAt !== current.updatedAt) {
          return false;
        }
        if (prev.name !== current.name) {
          return false;
        }

        // Для сложного JSON объекта body используем более глубокое сравнение
        // Проверяем только существование body и равенство версий/time если они есть
        if (prev.body && current.body) {
          if (typeof prev.body === 'object' && typeof current.body === 'object') {
            // Если это объекты EditorJS, сравниваем их по времени обновления, если оно есть
            const prevBody = prev.body as any;
            const currentBody = current.body as any;

            const prevTime = prevBody.time;
            const currentTime = currentBody.time;
            if (prevTime && currentTime && prevTime !== currentTime) {
              return false;
            }
            // Можно также добавить сравнение версии, если она есть
            const prevVersion = prevBody.version;
            const currentVersion = currentBody.version;
            if (prevVersion && currentVersion && prevVersion !== currentVersion) {
              return false;
            }
          } else if (JSON.stringify(prev.body) !== JSON.stringify(current.body)) {
            // Для других типов JSON данных используем строковое представление
            return false;
          }
        } else if (prev.body !== current.body) {
          return false;
        }

        return true;
      })
    );
  }
}
